import { AspectRatioDropdown, aspectRatioOptions, AspectRatioValue } from '@/components/AspectRatioDropdown/AspectRatioDropdown';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useTemplateFilters, useTemplates } from '@/pages/AdvancedEditor/components/panels/templates/hooks';
import { useTemplatesTagList } from '@/pages/AdvancedEditor/components/panels/templates/hooks/useTemplatesTagList';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import { CropMode } from '@/utils/types';
import { IProjectTemplate, VisibilityFilter } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogTitle, Divider, FormControlLabel, IconButton, Radio, RadioGroup } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMemo, useState } from 'react';
import 'react-photo-album/masonry.css';
import { RATIOS, TILE_SPACING } from '../constants';
import { SidebarFilter } from './SidebarFilter';
import { TemplateGalleryContent } from './TemplateGalleryContent';
import { TemplateSearch } from './TemplateSearch';
import { TEMPLATE_WIDTH } from './TemplateTile';

const Container = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    position: absolute;
    background: ${({ theme }) => theme.colors.black[50]};
`;

const Sidebar = styled.div`
    width: 280px;
    min-width: 200px;
    max-width: 280px;
    padding: 24px;
    background: ${({ theme }) => theme.colors.white};
    border-right: 1px solid #e0e0e0;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
`;

const MainContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 600px;
    position: relative;
`;

const Header = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 24px;
    flex-shrink: 0;
`;

const ModalTitle = styled(DialogTitle)`
    font-weight: bold;
    padding: 0 !important;
    margin: 0;
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    right: 24px;
    top: 24px;
`;

const SearchContainer = styled.div`
    padding: 0 64px 0 24px;
    margin-bottom: 24px;
    flex-shrink: 0;
    flex-direction: row;
    display: flex;
`;

const ASPECT_RATIO_TO_CROP_MODE: Record<AspectRatioValue, CropMode | undefined> = {
    all: undefined,
    youtube: CropMode.Wide,
    'youtube-short': CropMode.Vertical,
    facebook: CropMode.Square,
    instagram: CropMode.Vertical,
    tiktok: CropMode.Vertical,
    linkedin: CropMode.Square,
    landscape: CropMode.Wide,
    portrait: CropMode.Vertical,
    square: CropMode.Square,
} as const;

interface Props {
    setSelectedTemplate: (template: IProjectTemplate | null, index: number | null) => void;
    from?: 'Home' | 'Videos';
    aspectRatio: AspectRatioValue;
    setAspectRatio: (aspectRatio: AspectRatioValue) => void;
    search: string;
    setSearch: (search: string) => void;
    filter: string;
    setFilter: (filter: string) => void;
}

export const TemplateGalleryList = ({
    setSelectedTemplate,
    from,
    aspectRatio,
    setAspectRatio,
    search,
    setSearch,
    filter,
    setFilter,
}: Props) => {
    const mixpanel = useAnalytics();
    const projectsTemplateAdmin = useFlag('projects_template_admin', false);
    const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>(VisibilityFilter.VISIBLE);
    const { data: templates = [], isLoading, error, refetch } = useTemplates({ visibility: visibilityFilter });
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const { close } = useTemplateGalleryModal();
    const { filteredTemplates } = useTemplateFilters({
        templates,
        search,
        tagFilter: filter,
        aspectRatio: aspectRatio === 'all' ? undefined : ASPECT_RATIO_TO_CROP_MODE[aspectRatio],
    });

    const tagList = useTemplatesTagList(templates);
    const tagOptions = useMemo(() => tagList.map(tag => ({ id: tag, label: tag, active: tag === filter })), [tagList, filter]);

    const handleFilterChange = (value: string) => {
        setFilter(filter === value ? '' : value);
    };

    const handleStartFromBlank = async () => {
        close();

        mixpanel?.track('templates-preview', {
            event: 'click',
            componentType: 'modal',
            templateName: 'start from blank',
            templateId: 'start from blank',
            aspectRatio,
            useCaseFilter: [filter],
            textSearch: search,
            index: 0,
        });

        await navigateToAdvancedEditor({
            mixPanelFrom: 'template-gallery-blank',
            options: { from },
        });
    };

    const handleAspectRatioChange = (e: SelectChangeEvent<string>) => {
        setAspectRatio(e.target.value as AspectRatioValue);
    };

    const selectedAspectRatio = aspectRatioOptions.find(option => option.value === aspectRatio) || aspectRatioOptions[0];

    const photos = useMemo(
        () =>
            filteredTemplates.map(template => {
                const aspectRatio = RATIOS[template.cropMode] ?? RATIOS[CropMode.Wide];
                const imageHeight = TEMPLATE_WIDTH / aspectRatio;

                return {
                    ...template,
                    key: template.id,
                    src: template.scenesThumbnailsUrls[0],
                    width: TEMPLATE_WIDTH,
                    height: imageHeight + TILE_SPACING.BOTTOM_PADDING + TILE_SPACING.TITLE_MARGIN_TOP,
                    imageHeight,
                };
            }),
        [filteredTemplates]
    );

    return (
        <Container>
            <Sidebar>
                <ModalTitle>Create a video</ModalTitle>
                <Divider />
                <AspectRatioDropdown
                    title="Aspect ratio"
                    selectedAspectRatio={selectedAspectRatio}
                    onAspectRatioChange={handleAspectRatioChange}
                />
                {tagOptions.length > 0 && (
                    <SidebarFilter title="Use case" filter={filter} options={tagOptions} onChange={handleFilterChange} />
                )}
            </Sidebar>
            <MainContent>
                <Header>
                    <CloseButton onClick={close} aria-label="close">
                        <CloseIcon color="secondary" fontSize="large" />
                    </CloseButton>
                </Header>
                <SearchContainer>
                    <TemplateSearch search={search} setSearch={setSearch} location="modal" />
                    {projectsTemplateAdmin && (
                        <RadioGroup
                            row
                            value={visibilityFilter}
                            style={{ marginLeft: '24px' }}
                            onChange={e => setVisibilityFilter(e.target.value as VisibilityFilter)}>
                            <FormControlLabel value={VisibilityFilter.ALL} control={<Radio />} label="All" />
                            <FormControlLabel value={VisibilityFilter.VISIBLE} control={<Radio />} label="Visible" />
                            <FormControlLabel value={VisibilityFilter.HIDDEN} control={<Radio />} label="Hidden" />
                        </RadioGroup>
                    )}
                </SearchContainer>
                <TemplateGalleryContent
                    isLoading={isLoading}
                    isError={!!error}
                    photos={photos}
                    setSelectedTemplate={setSelectedTemplate}
                    onStartFromBlank={handleStartFromBlank}
                    searchQuery={search}
                    onRetry={refetch}
                />
            </MainContent>
        </Container>
    );
};
